import classNames from 'clsx';
import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';

import Toggle from '@swf/components/Inputs/Toggle';
import ToggleGroup from '@swf/components/Inputs/ToggleGroup';
import { EulaType, FeatureFlag, Site } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import { required } from '@swf/core/utils/validators';

import FirstCheckbox from './FirstCheckbox/FirstCheckbox';
import styles from './RegulatoryCheckboxes.scss';
import useLabel from './hooks/useLabel';

const additionalEulaTypes = {
  stars: {
    [Site.IT]: [EulaType.GamingAccAgreement],
    [Site.BG]: [EulaType.GameRulesConditions],
    [Site.IN]: [EulaType.IndianStatesRestrictions]
  },
  first: {
    [Site.IT]: [EulaType.GamingAccAgreement],
    [Site.BG]: [EulaType.GameRulesConditions],
    [Site.IN]: [EulaType.IndianStatesRestrictions],
    [Site.CAON]: [EulaType.IAmEligibleToPlay]
  }
};

export const getEulaTypes = (
  variant: Props['variant'],
  siteId: Site,
  separatedEulaPrivacyPolicy: boolean
): EulaType[] => {
  const result = separatedEulaPrivacyPolicy
    ? [EulaType.Eula, EulaType.Privacy]
    : [EulaType.CombinedEulaPrivacy];
  return [...result, ...(additionalEulaTypes[variant]?.[siteId] || [])];
};

const rmfConfig = [
  { eulaType: EulaType.PlayerIsNotPEP, isRequired: false },
  { eulaType: EulaType.PlayerIsNot3Party, isRequired: false },
  { eulaType: EulaType.InformationIsComplete, isRequired: true }
];

export interface Props {
  variant: 'first' | 'rmf' | 'stars';
  onEulaLinkClick?: () => void;
  onPrivacyLinkClick?: () => void;
}

const RegulatoryCheckboxes: React.FC<Props> = ({
  variant,
  onEulaLinkClick,
  onPrivacyLinkClick
}) => {
  const siteId = useSelector(select.site.getId);
  const separatedEulaPrivacyPolicy = useFeature(FeatureFlag.SeparatedEulaPrivacyPolicy);
  const getLabel = useLabel(onEulaLinkClick, onPrivacyLinkClick);
  const eulaTypes = getEulaTypes(variant, siteId, separatedEulaPrivacyPolicy);

  return (
    <>
      {variant === 'first' && (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {eulaTypes.map((eulaType: EulaType) => (
            <Fragment key={eulaType}>
              <FirstCheckbox
                eulaType={eulaType}
                onEulaLinkClick={onEulaLinkClick}
                onPrivacyLinkClick={onPrivacyLinkClick}
              />
            </Fragment>
          ))}
        </>
      )}
      {variant === 'rmf' && (
        <>
          {rmfConfig.map((box) => (
            <Fragment key={box.eulaType}>
              <FirstCheckbox
                eulaType={box.eulaType}
                variant={variant}
                isRequired={box.isRequired}
              />
            </Fragment>
          ))}
        </>
      )}
      {variant === 'stars' && (
        <ToggleGroup id="checkboxes" label="">
          <Toggle
            type="checkbox"
            id={EulaType.MarketingMailList}
            name={EulaType.MarketingMailList}
            label={getLabel('stars', EulaType.MarketingMailList)}
            className={styles.label}
          />
          {eulaTypes.map((eulaType: EulaType) => (
            <Toggle
              id={eulaType}
              key={eulaType}
              name={eulaType}
              type="checkbox"
              validators={[required()]}
              label={getLabel('stars', eulaType)}
              className={classNames({
                [styles.label]: [EulaType.Eula, EulaType.IndianStatesRestrictions].includes(
                  eulaType
                )
              })}
            />
          ))}
        </ToggleGroup>
      )}
    </>
  );
};

export default RegulatoryCheckboxes;
